import type { NLocale, NDateLocale } from 'naive-ui'

import type { LangEnum } from '@/constants/locales'
import { nUILanguageModuleMap, supportedLangsByCode, supportedLangsByName } from '@/constants/locales'
import { GtmEvent } from '@/types/gtm'
import { checkLangSupported } from '@/utils/intl'

import { useRegionStore } from '@/store/region'

export function useRegionNames() {
  const { locale } = useI18n()
  const regionNames = computed(() => new Intl.DisplayNames([locale.value], { type: 'region' }))

  function getRegionName(countryCode = 'TW') {
    try {
      return regionNames.value.of(countryCode) ?? ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  return {
    getRegionName,
  }
}

export function useNUILocale() {
  const { locale } = useI18n()
  const nUILocale = ref<Nullable<NLocale>>(null)
  const nUIDateLocale = ref<Nullable<NDateLocale>>(null)

  watch(locale, updateNUILocale, { immediate: true })

  async function updateNUILocale() {
    try {
      const [commonModule, dateModule] = await nUILanguageModuleMap[locale.value as LangEnum]()

      /**
       * 即時更新會踩到 nuxt 太頻繁更新 UI 的 bug，所以延遲一秒更新，預期 vue 升到 3.4 可以修正。
       * error msg: "TypeError: Cannot read properties of null (reading 'parentNode')"
       */
      setTimeout(() => {
        ;[nUILocale.value, nUIDateLocale.value] = [commonModule, dateModule]
      }, 1000)
    } catch (error: any) {
      console.warn(`[useNUILocale] locale "${locale.value}" update failed, error: ${error.message}`)
    }
  }

  return { nUILocale, nUIDateLocale }
}

export function useLangSelector() {
  const nuxtApp = useNuxtApp()
  const { locale, setLocale } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const { loadLocaleMessages } = useLocaleMessages()

  const regionStore = useRegionStore()

  // Use API region languages `code` (e.g. "zh-HK") to map to FE locale `name`
  const allowLangOptions = computed(() => {
    const languages = regionStore.currentRegion?.languages ?? []

    return languages.flatMap(lang => {
      if (!lang.code || !lang.name) {
        return []
      }

      // BE `code` (e.g. 'zh-HK') corresponds to FE `name`; FE `code` is the short locale id (e.g. 'zh-hk')
      const supported = supportedLangsByName[lang.code]

      return supported ? [{ label: lang.name, value: supported.code as LangEnum }] : []
    })
  })
  const selectedLangOption = computed(() => allowLangOptions.value.find(option => option.value === locale.value))

  async function changeLang(lang: LangEnum) {
    if (!lang || lang === locale.value || !checkLangSupported(lang)) {
      return
    }

    const path = replaceLangInPath(route.fullPath, lang)

    await loadLocaleMessages(lang)
    await setLocale(lang)
    router.push({ path, query: route.query, hash: route.hash })
  }

  async function handleLangLabelClick(langOption: Record<string, string>, fn = () => {}) {
    await changeLang(langOption.value as LangEnum)

    // `LANGUAGE_CHANGED` event records a real click from the user, so that's why it isn't placed in the `watch`
    nuxtApp.$trackEvent({
      event: GtmEvent.LANGUAGE_CHANGED,
      payload: {
        toLangName: supportedLangsByCode[langOption.value].name,
      },
    })

    fn()
  }

  return { locale, selectedLangOption, allowLangOptions, changeLang, handleLangLabelClick }
}

export function useUpperCase(str: ReadonlyRef<string>) {
  const { localeProperties } = useI18n()

  const localeUpperCaseString = computed(() => {
    if (!str.value) {
      return ''
    }

    try {
      const defaultLocaleIso = localeProperties.value.language
      const res = str.value.toLocaleUpperCase(defaultLocaleIso)
      return res
    } catch (error) {
      console.error(error)
      return ''
    }
  })

  return {
    localeUpperCaseString,
  }
}

export function useLowerCase(str: ReadonlyRef<string>) {
  const { localeProperties } = useI18n()

  const localeLowerCaseString = computed(() => {
    if (!str.value) {
      return ''
    }
    try {
      const defaultLocaleIso = localeProperties.value.language
      const res = str.value.toLocaleLowerCase(defaultLocaleIso)
      return res
    } catch (error) {
      console.error(error)
      return ''
    }
  })

  return {
    localeLowerCaseString,
  }
}
